/**
 * The coupons on offer, from `GET /api/v1/referrals/`.
 *
 * What the dialog behind "View coupons" is drawn from, and a convenience
 * rather than an authority. What a code is worth — and whether it holds on the
 * term being bought at all — is decided by the quote and by nothing on this
 * side. The list only saves somebody typing a code they were given elsewhere,
 * which is why a checkout whose coupon list failed to load is still a checkout
 * that can take a coupon. Which terms a row covers is read where the row says
 * so, and only to pick the button the dialog draws — see `cyclesOf`.
 *
 * **Documented public; not public in practice.** The reference says the route
 * takes no login, because it exists for the visitor opening a partner's link
 * before they have an account. The deployed API answers `401` without a bearer
 * token, so one goes up wherever there is one to send — see `getToken` below,
 * which is optional precisely so the signed-out case the document describes
 * still has a way to call this if the backend ever matches its own docs.
 *
 * It is throttled to 30 requests a minute per IP, so it is asked when the
 * dialog is opened and not on every checkout.
 *
 * **`source=NAURIST` goes up on every call**, and `referral_code` with it for
 * a practice that came through a partner's link — the code off
 * `workspace.referral`, which only `GET /onboarding/workspace/` publishes. See
 * `useWorkspaceReferral`.
 *
 * `source` narrows **`coupons`** to one issuer, so with it set that array is
 * Naurist's own and the partner's row is not in it. It does not narrow the
 * rest of the answer: `available`, `coupon_code`, `discount_percent` and
 * `partner` still describe the referring partner's coupon, and that is where
 * the partner's row is rebuilt from — see `partnerCouponOf`. It leads the
 * list, as it would have done on the unfiltered array.
 *
 * The copy on each row is written here, from the figures the server sent.
 * There is no `headline` or `description` on this response — the old
 * `/billing/coupons/` shape had both — so "Flat 10% off" is this app's
 * sentence about the backend's `PERCENT` and `"10.00"`, not a string it was
 * handed.
 */

import { api, API_ENDPOINTS } from "../../../lib/api";
import type { GetToken } from "../../auth/types/auth.types";
import { formatMoney } from "../pricing";
import type { BillingCycleId, Coupon } from "../types/billing.types";
import type {
  ReferralCouponResponse,
  ReferralLookupResponse,
  ReferralOffer,
  ReferralPartner,
} from "../types/referrals.types";

/**
 * A decimal string as a number, or `null`.
 *
 * `null` rather than 0 for an unreadable figure, unlike the money parsers in
 * the services around this one: those land on 0 because a price has to be
 * drawn as something, and here a discount nobody can state is a row that
 * simply says less about itself.
 */
const toAmount = (raw: string | null | undefined): number | null => {
  const value = Number.parseFloat(raw ?? "");
  return Number.isFinite(value) ? value : null;
};

/**
 * A percentage as the row says it: `"10.00"` reads "10%", `"7.50"` reads
 * "7.5%".
 *
 * The trailing zeros go because they are an artefact of a decimal column, not
 * a claim about precision — "10.00% off" is a sentence nobody writes.
 */
const percentLabel = (raw: string | null | undefined): string | null => {
  const value = toAmount(raw);
  return value === null ? null : `${Number(value.toFixed(2))}`;
};

/**
 * What a coupon is worth, in a line.
 *
 * `PERCENT` and `FLAT` are the two the backend issues, and an unrecognised
 * kind falls through to an empty headline rather than a guess — the row still
 * carries its code, which is the thing somebody came to the dialog to copy.
 */
const headlineOf = (row: ReferralCouponResponse): string => {
  const kind = (row.discount_type ?? "").toUpperCase();

  if (kind === "PERCENT") {
    const percent = percentLabel(row.discount_percent);
    if (!percent) return "";
    const ceiling = toAmount(row.max_discount_amount);
    // A percentage with a ceiling is two facts and has to read as two, or the
    // first one is a promise the second quietly breaks.
    return ceiling !== null
      ? `${percent}% off, up to ${formatMoney(ceiling)}`
      : `Flat ${percent}% off`;
  }

  if (kind === "FLAT") {
    const amount = toAmount(row.discount_amount);
    return amount !== null ? `${formatMoney(amount)} off` : "";
  }

  return "";
};

/** What goes up as `source` on every lookup. Naurist's own coupons are the
 *  ones the array can be drawn from; the partner's arrives beside it. */
const NAURIST = "NAURIST";

const toPartner = (
  response: ReferralLookupResponse
): ReferralPartner | null => {
  const name = response.partner?.name?.trim() ?? "";
  const brandName = response.partner?.brand_name?.trim() ?? "";
  return name || brandName ? { name, brandName } : null;
};

/**
 * The line under a partner's coupon, saying why it is being offered at all.
 *
 * Written here, from the partner's name and what the coupon takes off, because
 * the lookup sends no description — the same reason `headlineOf` writes the
 * headline. Somebody handed a code by whoever referred their dietitian should
 * see whose it is, and the partner is the only thing on the response that
 * explains the row.
 *
 * What it does **not** say is who may use it. The design's own line reads
 * "...for new users who joined through Riya's referral", and nothing on this
 * endpoint says the coupon is restricted to new users — asserting it here
 * would be this app inventing a rule about who gets a discount. The quote is
 * what accepts or refuses a code, with the reason to print.
 */
const partnerLine = (
  row: ReferralCouponResponse,
  partner: ReferralPartner | null
): string => {
  const who = partner?.name?.trim() || partner?.brandName?.trim();
  if (!who) return "";

  const percent = percentLabel(row.discount_percent);
  return percent
    ? `${percent}% off any plan, through ${who}'s referral.`
    : `Offered through ${who}'s referral.`;
};

/**
 * Reads a backend plan **code** — `PRO_YEARLY` — as one of this app's terms,
 * or `null` where the price list in hand holds no such plan.
 *
 * Passed down from the screen rather than worked out here, because only the
 * price list can answer it: a code is the plan's own name and the term is this
 * app's reading of the `interval` beside it. See `plansService`.
 */
export type CycleOfCode = (code: string) => BillingCycleId | null;

/** The resolver for a caller with no price list to hand: nothing resolves, and
 *  a row falls back to what is known about its code. */
const NO_PLANS: CycleOfCode = () => null;

/**
 * Which term a coupon is good on, where the wire does not say and the offer is
 * one Naurist is running by name.
 *
 * The lookup carries `applicable_plan_codes` on a row only when the backend
 * has been taught to send it, and today it mostly has not — so these live
 * offers would arrive as "holds on every term" and the dialog would offer to
 * apply a yearly code to a month, which the quote then refuses with a message
 * nobody asked for. A Naurist code names its own term before it names its
 * discount, and that first part is what is read here.
 *
 * **Matched on the prefix, not on the whole code, and that is the point.**
 * This was a table of whole codes — `HYEARLY10`, `YEARLY15` — and it went
 * quietly stale the day the discounts were re-cut to `HYEARLY5` and
 * `YEARLY10`: every row fell through to "holds everywhere" and every button in
 * the dialog said "Apply", including on the one-month checkout. A prefix
 * survives the percentage changing, which is the part of these codes that
 * actually changes.
 *
 * `HYEARLY` and `HALFYEARLY` are listed ahead of `YEARLY` for a reader rather
 * than for the match — neither of them starts with `YEARLY`, so `startsWith`
 * cannot confuse the two.
 *
 * **A stand-in, and only consulted when the row names no plans at all.** The
 * moment a row arrives with `applicable_plan_codes` on it, that is what is
 * read and this table is not looked at — so the backend taking the field over
 * is a deletion here, not a migration. A code matching no prefix — a partner's
 * `NAU-SAMEER-TH7`, a one-off like `FLAT-300` — holds everywhere, exactly as
 * before.
 */
export const COUPON_TERM_PREFIXES: ReadonlyArray<
  readonly [string, BillingCycleId]
> = [
  ["HYEARLY", "half-yearly"],
  ["HALFYEARLY", "half-yearly"],
  ["YEARLY", "yearly"],
  ["QUARTERLY", "quarterly"],
  ["MONTHLY", "monthly"],
];

/**
 * The term a bare coupon code names, or none.
 *
 * None for anything that is not one of Naurist's own term-named offers, which
 * reads as "holds everywhere" upstream — the generous way round, and harmless,
 * because the quote is what refuses a code that does not hold.
 */
export const termsOfCode = (code: string): BillingCycleId[] => {
  const upper = code.trim().toUpperCase();
  const hit = COUPON_TERM_PREFIXES.find(([prefix]) => upper.startsWith(prefix));
  return hit ? [hit[1]] : [];
};

/**
 * The terms one row holds for: what it says, or what is known about it.
 *
 * Plan **codes** are what the wire names a term by, and a code is only a term
 * against the price list in hand — so resolving one needs `cycleOfCode`, which
 * the screen passes down from the plans it has already loaded. A code this
 * build has never seen resolves to nothing, which reads as "holds everywhere":
 * the generous way round, and harmless, because the quote is what refuses a
 * coupon that does not hold.
 */
const cyclesOf = (
  row: Pick<ReferralCouponResponse, "coupon_code" | "applicable_plan_codes">,
  cycleOfCode: CycleOfCode
): BillingCycleId[] => {
  const named = row.applicable_plan_codes ?? [];

  if (named.length === 0) {
    return termsOfCode(row.coupon_code ?? "");
  }

  return named
    .map((code) => cycleOfCode(code))
    .filter((cycle): cycle is BillingCycleId => cycle !== null);
};

/**
 * One row of the offer, as the dialog reads it.
 *
 * A partner's row carries the line above. Naurist's own rows need none: they
 * are simply what Naurist is offering, and the headline already says what it
 * is worth.
 *
 * `cycles` is what the row names, or what is known about its code where it
 * names nothing — see `cyclesOf`. Empty reads as "holds everywhere", which is
 * the generous way round and harmless: the quote is what refuses a code that
 * does not hold, and it refuses it with the reason to print. What the terms
 * decide on this side is only which button the dialog draws.
 */
export const toCoupon = (
  row: ReferralCouponResponse,
  partner: ReferralPartner | null,
  cycleOfCode: CycleOfCode = NO_PLANS
): Coupon => {
  const fromPartner = (row.source ?? "").toUpperCase() === "PARTNER";

  return {
    code: (row.coupon_code ?? "").trim().toUpperCase(),
    headline: headlineOf(row),
    description: fromPartner ? partnerLine(row, partner) : "",
    cycles: cyclesOf(row, cycleOfCode),
  };
};

/**
 * The referring partner's coupon, rebuilt from the fields beside the array.
 *
 * It is rebuilt rather than read because every lookup goes up with
 * `source=NAURIST`, which keeps the partner's own row out of `coupons`. What
 * it cannot keep out is `available`, `coupon_code`, `discount_percent` and
 * `partner`, which describe exactly that coupon — so nothing here is invented,
 * only moved.
 *
 * **Only while `available`.** That is the server's verdict over a signed
 * agreement, an effective date that has arrived and a backing coupon still
 * active; a coupon offered while it is false is one the quote would refuse,
 * which is the one thing this dialog must never do.
 *
 * Partner coupons discount by percent — `discount_percent` is the only figure
 * the lookup carries for one — so there is no flat case to handle here.
 */
const partnerCouponOf = (
  response: ReferralLookupResponse,
  partner: ReferralPartner | null,
  cycleOfCode: CycleOfCode = NO_PLANS
): Coupon | null => {
  const code = (response.coupon_code ?? "").trim().toUpperCase();
  if (response.available !== true || !code) return null;

  const percent = percentLabel(response.discount_percent);

  return {
    code,
    headline: percent ? `Flat ${percent}% off` : "",
    // The same line a row off the array would carry — see `partnerLine`.
    description: partnerLine(
      { coupon_code: code, source: "PARTNER", discount_type: "PERCENT",
        discount_percent: response.discount_percent },
      partner
    ),
    // The fields this row is rebuilt from carry no plan codes, so this is the
    // code's own terms where it has any and "holds everywhere" where it has
    // none — which is every partner coupon issued so far.
    cycles: cyclesOf({ coupon_code: code }, cycleOfCode),
  };
};

/**
 * The whole offer: the partner's coupon where there is a usable one, then
 * Naurist's.
 *
 * The two halves come from two places on the response, which is what
 * `source=NAURIST` costs — see `partnerCouponOf`. The order is the one the
 * unfiltered array would have had, and the one RF-16 describes.
 *
 * A row with no code is dropped, because it cannot be applied, cannot be
 * copied, and a blank line in a list of offers is worse than a shorter list.
 */
export const toReferralOffer = (
  response: ReferralLookupResponse,
  cycleOfCode: CycleOfCode = NO_PLANS
): ReferralOffer => {
  const partner = toPartner(response);
  const partnerCoupon = partnerCouponOf(response, partner, cycleOfCode);

  const listed = (response.coupons ?? [])
    .filter((row) => !!row?.coupon_code)
    .map((row) => toCoupon(row, partner, cycleOfCode))
    .filter((coupon) => !!coupon.code)
    // A backend that ever stops filtering by `source`, or one asked without
    // it, would send the partner's row here too. Dropped rather than drawn
    // twice.
    .filter((coupon) => coupon.code !== partnerCoupon?.code);

  return {
    referralCode: response.referral_code?.trim() || null,
    available: response.available === true,
    reason: response.reason?.trim() || null,
    partner,
    coupons: partnerCoupon ? [partnerCoupon, ...listed] : listed,
  };
};

/**
 * Asks what is on offer.
 *
 * `source=NAURIST` goes up every time, and `referral_code` with it where the
 * practice has one. A practice that came to Naurist directly sends only the
 * first, and gets a 200 holding Naurist's own coupons — not an error.
 *
 * `getToken` is passed where the caller has a session, which is every caller
 * in this app today: the deployed endpoint answers `401` without one. It is
 * optional rather than required because the document describes a signed-out
 * caller — the login page showing a referrer's offer before sign-up — and that
 * caller has no token to give.
 *
 * An unknown or deleted referral code is `404 referral_code_not_found`, which
 * is a real failure and is thrown: a practice told it was referred, whose code
 * the backend has never heard of, is something the dialog should say rather
 * than silently quoting them Naurist's own list instead.
 */
export async function fetchReferralOffer(
  getToken?: GetToken | null,
  referralCode?: string | null,
  signal?: AbortSignal,
  cycleOfCode: CycleOfCode = NO_PLANS
): Promise<ReferralOffer> {
  const code = referralCode?.trim();

  const response = await api.get<ReferralLookupResponse | null>(
    API_ENDPOINTS.referrals,
    {
      token: getToken ? await getToken() : null,
      query: {
        source: NAURIST,
        ...(code ? { referral_code: code } : {}),
      },
      signal,
    }
  );

  return toReferralOffer(response ?? {}, cycleOfCode);
}
